import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { normalizeRichTextUrl } from '../../lib/richtext/htmlSanitizer';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Modal = styled.div`
  background: var(--background-primary, #ffffff);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 420px;
  animation: slideUp 0.2s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Header = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #e6e1da);
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(176, 141, 87, 0.15), rgba(176, 141, 87, 0.05));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #1a1a1a);
  font-family: 'Inter', system-ui, sans-serif;
`;

const Body = styled.div`
  padding: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary, #666666);
  margin-bottom: 0.5rem;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  font-size: 1rem;
  font-family: 'Inter', system-ui, sans-serif;
  border: 1.5px solid var(--border-color, #e6e1da);
  border-radius: 8px;
  background: var(--background-primary, #ffffff);
  color: var(--text-primary, #1a1a1a);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color, #b08d57);
    box-shadow: 0 0 0 3px rgba(176, 141, 87, 0.1);
  }

  &::placeholder {
    color: var(--text-muted, #999999);
  }
`;

const Hint = styled.p`
  margin: 0.5rem 0 0 0;
  font-size: 0.8rem;
  color: var(--text-muted, #999999);
  font-family: 'Inter', system-ui, sans-serif;
`;

const Footer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color, #e6e1da);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'Inter', system-ui, sans-serif;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 90px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  border: 1.5px solid var(--border-color, #e6e1da);
  color: var(--text-secondary, #666666);

  &:hover:not(:disabled) {
    background: var(--surface-color, #f5f5f5);
    border-color: var(--text-secondary, #666666);
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--primary-color, #b08d57);
  border: 1.5px solid var(--primary-color, #b08d57);
  color: white;

  &:hover:not(:disabled) {
    background: var(--primary-hover, #9a7a4a);
    border-color: var(--primary-hover, #9a7a4a);
  }
`;

const RemoveButton = styled(Button)`
  background: transparent;
  border: 1.5px solid #dc2626;
  color: #dc2626;
  margin-right: auto;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
`;

export default function LinkModal({ isOpen, onClose, onSubmit, onRemove, initialUrl = '' }) {
  const [url, setUrl] = useState(initialUrl);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef(null);
  const isEditing = Boolean(initialUrl);

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl);
      setErrorMsg('');
      // Focus input after animation
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [isOpen, initialUrl]);

  const handleSubmit = useCallback(() => {
    const normalized = normalizeRichTextUrl(url);
    if (!normalized) {
      setErrorMsg('Please enter a valid URL (e.g. example.com)');
      return;
    }
    onSubmit(normalized);
  }, [url, onSubmit]);

  // Handle escape key globally, but keep submit scoped to the modal controls so
  // paste/IME events inside the URL field cannot accidentally close the dialog.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleRemove = () => {
    onRemove();
  };

  if (!isOpen) return null;

  return (
    <Overlay onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <Modal onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <Header>
          <IconWrapper>🔗</IconWrapper>
          <Title>{isEditing ? 'Edit Link' : 'Add Link'}</Title>
        </Header>

        <Body>
          <Label htmlFor="link-url">URL</Label>
          <Input
            ref={inputRef}
            id="link-url"
            type="text"
            inputMode="url"
            value={url}
            onChange={(e) => { setUrl(e.target.value); if (errorMsg) setErrorMsg(''); }}
            onPaste={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter' && url.trim()) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="https://example.com"
            autoComplete="url"
            aria-invalid={Boolean(errorMsg)}
            aria-describedby="link-url-hint"
          />
          <Hint id="link-url-hint" style={errorMsg ? { color: '#dc2626' } : undefined}>
            {errorMsg || 'Enter a web address. "https://" will be added automatically if missing.'}
          </Hint>
        </Body>

        <Footer>
          {isEditing && (
            <RemoveButton type="button" onClick={handleRemove}>
              Remove
            </RemoveButton>
          )}
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={!url.trim()}
          >
            {isEditing ? 'Update' : 'Add Link'}
          </PrimaryButton>
        </Footer>
      </Modal>
    </Overlay>
  );
}
